import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import * as cssTable from '@delmonicos/shared-design-system-react/src/components/table/table.module.scss';
import { GatewayTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/outputs/gateway-token.model';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { TokenDeleteController } from '@app/feature/token/token-delete/token-delete.controller';
import { TokenContextProvider } from '@app/feature/token/token.context';

export interface TokenListActionsCellProps
  extends CellContext<GatewayTokenModel, unknown> {}

export const TokenListActionsCell = (
  props: TokenListActionsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { row } = props;
  const navigate = useNavigate();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canRead =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.TOKENS,
      action: ActionsByResource.TOKENS.READ,
    });

  const canDelete =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.TOKENS,
      action: ActionsByResource.TOKENS.DELETE,
    });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <TokenContextProvider data={row.original}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end">
        {!canRead && <div />}
        {canRead && (
          <ButtonIcon
            color={ButtonColor.Ghost}
            icon="eye"
            onClick={() =>
              navigate(
                generatePath(APP_PATH.TOKEN.DETAILS, {
                  id: row.original.id,
                }),
              )
            }
            className={cssTable.displayOnHover}
          />
        )}

        {canDelete && (
          <>
            <ButtonIcon
              icon="trash-01"
              color={ButtonColor.Ghost}
              onClick={() => setActionDeleteIsDialogOpen(true)}
              className={cssTable.displayOnHover}
            />
            <Dialog
              onClose={() => setActionDeleteIsDialogOpen(true)}
              open={actionDeleteIsDialogOpen}>
              <Dialog.Header
                title={t('$.token-delete.title')}
                icon="map-01"
                onClose={() => setActionDeleteIsDialogOpen(false)}
              />
              <Dialog.Content>
                <TokenDeleteController
                  onCancel={() => setActionDeleteIsDialogOpen(false)}
                  onSuccess={() => setActionDeleteIsDialogOpen(false)}
                />
              </Dialog.Content>
            </Dialog>
          </>
        )}
      </Stack>
    </TokenContextProvider>
  );
};
